import Box from "@mui/material/Box";
import { format } from "date-fns";
import { FormikValues } from "formik";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { formatDate } from "../../../../../utils/helpers";
import { SelectedWorkerSlot } from "../../../types/direct-assignment";
import JobItem from "../../job-role-item";
import Notice from "../../shift-details/notice";
import AssignWorkersStep from "../direct-assign-workers/assign-workers-step";
import { DirectAssignShift } from "../direct-assign-workers/use-direct-assign-picker";
import { WorkersPropsV2 } from "../shift-details-dialog/types";

// Formik field that holds the Workers picked to cover. Sent as workerSlots
// with the approval and never on its own.
export const DIRECT_ASSIGN_FIELD = "directAssignWorkerSlots";

// The create Shift form's values; the fields read here are startTime,
// endTime, jobId, shiftSkills, numberOfWorkersNeeded, locationId,
// workCenterId, allowPartialSignups, isFlexiSignupEnabled,
// minFlexiSignupHours, allowedWorkerTypeIds and DIRECT_ASSIGN_FIELD
type CoverageValues = FormikValues;

interface CoverageSkill {
	skillId: number | string;
	skillName: string;
}

export const getCoverageShiftTimeLabel = (values: CoverageValues) => {
	if (!values.startTime || !values.endTime) return "";
	return `${formatDate({
		dateStr: new Date(values.startTime).toISOString(),
		formatType: "SHORT_DATE",
	})} ${format(new Date(values.startTime), "h:mm a")} - ${format(
		new Date(values.endTime),
		"h:mm a"
	)}`;
};

export const getCoverageJobRoleLabel = (
	values: CoverageValues,
	jobRoles: { id: number | string; name: string; hexColor: string }[]
): React.ReactNode => {
	const jobRole = (jobRoles ?? []).find(
		(job) => String(job.id) === String(values.jobId)
	);
	if (values.jobId && jobRole) return <JobItem jobRole={jobRole} />;
	return ((values.shiftSkills ?? []) as CoverageSkill[])
		.map((skill) => skill.skillName)
		.join(", ");
};

interface CoverageAssignNoticeProps {
	values: CoverageValues;
	setFieldValue: (field: string, value: unknown) => void;
	// Gated in, and the eligible list for these values has loaded
	isAvailable: boolean;
	onAssignWorkers: () => void;
}

// The row under Job Role / Skills that opens the picker. It also drops the
// pick whenever a value that decides who is eligible changes.
export const CoverageAssignNotice = ({
	values,
	setFieldValue,
	isAvailable,
	onAssignWorkers,
}: CoverageAssignNoticeProps) => {
	const { t } = useTranslation();
	const selection: SelectedWorkerSlot[] = useMemo(
		() => values[DIRECT_ASSIGN_FIELD] ?? [],
		[values]
	);
	const required = Number(values.numberOfWorkersNeeded) || 0;

	const eligibilityKey = JSON.stringify([
		values.jobId,
		((values.shiftSkills ?? []) as CoverageSkill[]).map(
			(skill) => skill.skillId
		),
		values.startTime,
		values.endTime,
		values.locationId,
		values.workCenterId,
		values.numberOfWorkersNeeded,
		values.allowPartialSignups,
		values.isFlexiSignupEnabled,
		values.allowedWorkerTypeIds,
	]);
	const previousEligibilityKey = useRef(eligibilityKey);
	useEffect(() => {
		if (previousEligibilityKey.current === eligibilityKey) return;
		previousEligibilityKey.current = eligibilityKey;
		if (selection.length > 0) setFieldValue(DIRECT_ASSIGN_FIELD, []);
	}, [eligibilityKey, selection.length, setFieldValue]);

	// Gated out (or the location changed to one that is): nothing to send
	useEffect(() => {
		if (!isAvailable && selection.length > 0) {
			setFieldValue(DIRECT_ASSIGN_FIELD, []);
		}
	}, [isAvailable, selection.length, setFieldValue]);

	if (!isAvailable) return null;

	return (
		<Box width='100%' mt={2}>
			<Notice
				variant='normal'
				message={
					selection.length > 0
						? t("scheduler_coverage-workers-assigned", {
							n: selection.length,
							m: required,
						})
						: t("scheduler_coverage-no-worker-assigned")
				}
				buttonLabel={
					selection.length > 0
						? t("common_edit")
						: t("scheduler_assign-workers")
				}
				buttonVariant={selection.length > 0 ? "text" : "outlined"}
				callToAction={onAssignWorkers}
			/>
		</Box>
	);
};

interface CoverageAssignStepProps {
	values: CoverageValues;
	setFieldValue: (field: string, value: unknown) => void;
	workers: WorkersPropsV2[];
	isLoading: boolean;
	isWorkerSubTypeEnabled: boolean;
	workcenters: { id: number; name: string; locationId: number }[];
	locations: { id: number; name: string }[];
	jobRoles: { id: number | string; name: string; hexColor: string }[];
	onClose: () => void;
}

export const CoverageAssignStep = ({
	values,
	setFieldValue,
	workers,
	isLoading,
	isWorkerSubTypeEnabled,
	workcenters,
	locations,
	jobRoles,
	onClose,
}: CoverageAssignStepProps) => {
	const { t } = useTranslation();
	const required = Number(values.numberOfWorkersNeeded) || 0;
	const workcenter = (workcenters ?? []).find(
		(wc) => String(wc.id) === String(values.workCenterId)
	);
	const location = (locations ?? []).find(
		(l) => String(l.id) === String(values.locationId)
	);

	const shift: DirectAssignShift | null = useMemo(
		() =>
			values.startTime && values.endTime
				? {
					startDateTime: new Date(values.startTime).toISOString(),
					endDateTime: new Date(values.endTime).toISOString(),
					numberOfWorkersNeeded: required,
					jobId: values.jobId || null,
					shiftWorkcenterName: workcenter?.name ?? "",
					shiftLocationName: location?.name ?? "",
					isPartialSignupEnabled: !!values.allowPartialSignups,
					isFlexiSignupEnabled: !!values.isFlexiSignupEnabled,
					minFlexiSignupHours: values.minFlexiSignupHours,
				}
				: null,
		[values, required, workcenter, location]
	);

	const handleSave = useCallback(
		(selection: SelectedWorkerSlot[]) => {
			setFieldValue(DIRECT_ASSIGN_FIELD, selection);
			onClose();
		},
		[setFieldValue, onClose]
	);

	return (
		<AssignWorkersStep
			shift={shift}
			workers={workers}
			isLoading={isLoading}
			initialSelection={values[DIRECT_ASSIGN_FIELD] ?? []}
			isWorkerSubTypeEnabled={isWorkerSubTypeEnabled}
			shiftTimeLabel={getCoverageShiftTimeLabel(values)}
			jobRoleLabel={getCoverageJobRoleLabel(values, jobRoles)}
			shiftDetailsLabel={[
				location?.name,
				workcenter?.name,
				t("scheduler_n-required-workers", { count: required }),
			]
				.filter(Boolean)
				.join(" · ")}
			subtitle={t("scheduler_assign-coverage-subtitle", { n: required })}
			onCancel={onClose}
			onSave={handleSave}
		/>
	);
};
